import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, X, ExternalLink, ArrowRight } from "lucide-react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface ClientNotification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  read: boolean;
  readAt: string | null;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [, setLocation] = useLocation();

  const { data: unreadCount } = useQuery<{ count: number }>({
    queryKey: ["/api/client/notifications/unread-count"],
    refetchInterval: 30000,
  });

  const { data: notifications } = useQuery<ClientNotification[]>({
    queryKey: ["/api/client/notifications"],
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/client/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client/notifications/unread-count"] });
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const count = unreadCount?.count || 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setOpen(!open)}
        className="relative"
        data-testid="button-notification-bell"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="badge-unread-count"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </Button>

      {open && (
        <Card className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-full sm:mt-2 sm:w-80 max-h-96 overflow-hidden z-50 shadow-lg">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-2 border-b">
            <CardTitle className="text-sm font-medium">Notifikationer</CardTitle>
            <Button size="icon" variant="ghost" onClick={() => setOpen(false)} className="h-6 w-6">
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <div className="overflow-y-auto max-h-72">
            {notifications && notifications.length > 0 ? (
              <div className="divide-y">
                {notifications.map((notif) => {
                  const handleClick = () => {
                    if (!notif.read) {
                      markReadMutation.mutate(notif.id);
                    }
                    setOpen(false);
                    if (notif.link) {
                      if (notif.link.startsWith("http")) {
                        window.open(notif.link, "_blank", "noopener,noreferrer");
                      } else {
                        setLocation(notif.link);
                      }
                    } else {
                      setLocation(`/notifications?id=${notif.id}`);
                    }
                  };
                  return (
                    <div
                      key={notif.id}
                      className={`p-3 text-sm hover-elevate cursor-pointer ${!notif.read ? "bg-primary/5" : ""}`}
                      onClick={handleClick}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${!notif.read ? "" : "text-muted-foreground"}`}>
                            {notif.title}
                          </p>
                          <p className="text-muted-foreground text-xs mt-0.5 line-clamp-2">{notif.body}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(notif.createdAt), "d MMM, HH:mm", { locale: sv })}
                            </p>
                            {notif.link && (
                              <span className="text-xs text-primary flex items-center gap-0.5">
                                {notif.link.startsWith("http") ? <ExternalLink className="h-3 w-3" /> : <ArrowRight className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </div>
                        {!notif.read && (
                          <div className="shrink-0">
                            <span className="h-2 w-2 bg-primary rounded-full block mt-1.5" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Inga notifikationer</p>
              </div>
            )}
          </div>
          <div className="border-t p-2">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => {
                setOpen(false);
                setLocation("/notifications");
              }}
              data-testid="button-view-all-notifications"
            >
              Visa alla notifikationer
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
